import type { EvaluationCheck, GameplayEvaluationReceipt } from "../shared/adapter";
import { PARTICLE_CAP } from "../shared/presentation";
import type { EndlessRunnerSpec } from "./spec";

export interface ScheduledObstacle {
  readonly index: number;
  readonly spawnSeconds: number;
  /** This obstacle's own travel speed, ramped from base toward the final percent. */
  readonly speed: number;
}

/** Geometry constants mirrored by the compiled game. */
export const RUNNER_GEOMETRY = { playerXRatio: 0.2, playerW: 28, playerH: 36, groundInset: 40 } as const;

/** The exact seeded, ramp-aware obstacle schedule both the evaluator and the compiled game share. */
export function obstacleSchedule(spec: EndlessRunnerSpec): readonly ScheduledObstacle[] {
  const { minGapSeconds, obstacleCount, obstacleSpeed } = spec.params;
  const ramp = (spec.params.speedRampPercent ?? 0) / 100;
  let state = spec.seed >>> 0;
  const random = () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);
  const obstacles: ScheduledObstacle[] = [];
  let cursor = minGapSeconds;
  for (let index = 0; index < obstacleCount; index += 1) {
    const progress = obstacleCount === 1 ? 0 : index / (obstacleCount - 1);
    obstacles.push({
      index,
      spawnSeconds: Number(cursor.toFixed(3)),
      speed: Number((obstacleSpeed * (1 + ramp * progress)).toFixed(3)),
    });
    cursor += minGapSeconds + random() * minGapSeconds;
  }
  return obstacles;
}

/** The danger window an obstacle opens at the player's fixed position, in absolute seconds. */
function dangerWindow(spec: EndlessRunnerSpec, obstacle: ScheduledObstacle): { readonly start: number; readonly end: number } {
  const playerX = spec.params.worldWidth * RUNNER_GEOMETRY.playerXRatio;
  const start = obstacle.spawnSeconds + (spec.params.worldWidth - playerX - RUNNER_GEOMETRY.playerW) / obstacle.speed;
  const end = obstacle.spawnSeconds + (spec.params.worldWidth - playerX + spec.params.obstacleWidth) / obstacle.speed;
  return { start, end };
}

/**
 * Proves clearability numerically per obstacle: each obstacle's danger
 * window (computed at its OWN ramped speed) must fit inside one jump's
 * hang time with margin, and consecutive danger windows must be far
 * enough apart that jumps never overlap -- checked pairwise on the real
 * schedule, since a speed ramp makes later obstacles arrive compressed
 * relative to their spawn gaps. The trace centers each jump's air time
 * over its danger window, the maximum-margin trigger.
 */
export function evaluateEndlessRunner(spec: EndlessRunnerSpec): GameplayEvaluationReceipt {
  const { gravity, jumpVelocity } = spec.params;
  const airTimeSeconds = (2 * jumpVelocity) / gravity;
  const schedule = obstacleSchedule(spec);
  const windows = schedule.map((obstacle) => dangerWindow(spec, obstacle));

  let worstCoverage = Infinity;
  for (const window of windows) {
    worstCoverage = Math.min(worstCoverage, airTimeSeconds / (window.end - window.start));
  }
  const clearsEveryObstacle = worstCoverage >= 1.15;

  let worstJumpGap = Infinity;
  for (let i = 1; i < windows.length; i += 1) {
    worstJumpGap = Math.min(worstJumpGap, windows[i]!.start - windows[i - 1]!.start);
  }
  const gapAllowsRecovery = windows.length < 2 || worstJumpGap >= airTimeSeconds;

  const trace = windows.map((window) => ({
    elapsedSeconds: Number(Math.max(0, window.start - (airTimeSeconds - (window.end - window.start)) / 2).toFixed(3)),
    action: { type: "jump" as const },
  }));

  const checks: EvaluationCheck[] = [
    { id: "jump-clears-obstacle", passed: clearsEveryObstacle, evidence: `${airTimeSeconds.toFixed(3)}s air time covers the tightest ramped danger window at ${(worstCoverage * 100).toFixed(0)}%` },
    { id: "gap-allows-recovery", passed: gapAllowsRecovery, evidence: `${(worstJumpGap === Infinity ? 0 : worstJumpGap).toFixed(3)}s tightest jump spacing vs ${airTimeSeconds.toFixed(3)}s air time` },
    { id: "presentation-budget", passed: true, evidence: `bounded live obstacles + ${PARTICLE_CAP} pooled particles fit the frame budget by construction` },
  ];
  return {
    version: 1,
    seed: spec.seed,
    passed: checks.every((check) => check.passed),
    checks,
    trace,
    metrics: {
      airTimeSeconds: Number(airTimeSeconds.toFixed(3)),
      worstCoveragePercent: Number((worstCoverage * 100).toFixed(1)),
      obstacleCount: schedule.length,
      speedRampPercent: spec.params.speedRampPercent ?? 0,
    },
  };
}
